import React, { useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { useSelector } from 'store';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { FilterState } from 'store/ducks/filter/types';
import FlexBox from '@bexs/dollar/components/Flex';
import { Field } from '@bexs/dollar/components/Form';
import IconClose from '@bexs/dollar-icons/Close';
import DatepickerWithIcon from 'components/Datepicker';
import {
  CardFilter,
  FormFilter,
  CardFilterClose,
} from 'pages/Balance/sections/FilterBox/styles';
import { FilterBoxProps } from 'pages/Balance/sections/FilterBox/types';
import Input from '@bexs/dollar/components/Input';
import { updateFilter } from 'store/ducks/filter/actions';
import { CustomButton } from 'styles/global';

const MAX_ALLOWED_RANGE_DATES = 61;

const FilterBox: React.FC<FilterBoxProps> = ({ close, open }) => {
  const dispatch = useDispatch();

  const {
    periodStart,
    periodEnd,
    document: documentNumber,
  } = useSelector().filter;

  const { handleSubmit, getFieldProps, setFieldValue, values } =
    useFormik<FilterState>({
      onSubmit: (values) => {
        dispatch(updateFilter(values));
        close();
      },
      initialValues: {
        periodStart: periodStart,
        periodEnd: periodEnd,
        document: documentNumber || '',
      },
    });

  const handleSelectDates = useCallback((from: Date, to: Date) => {
    const fromObject = dayjs(from);
    const toObject = dayjs(to);
    if (toObject.diff(fromObject, 'days') > MAX_ALLOWED_RANGE_DATES) {
      alert(`Max range allowed is ${MAX_ALLOWED_RANGE_DATES} days`);
      return Promise.reject();
    }
    setFieldValue('periodStart', from);
    setFieldValue('periodEnd', to);
  }, []);

  const keyboardNavigation = useCallback(
    ({ key }: KeyboardEvent) => key === 'Escape' && open && close(),
    [open, close]
  );

  useEffect(() => {
    document.addEventListener('keydown', keyboardNavigation);
    return () => document.removeEventListener('keydown', keyboardNavigation);
  }, [keyboardNavigation]);

  if (!open) return null;

  return (
    <CardFilter
      title="Filters"
      headerRightContent={
        <CardFilterClose aria-label="Close filters" onClick={close}>
          <IconClose />
        </CardFilterClose>
      }
    >
      <FormFilter id="filter-form" onSubmit={handleSubmit}>
        <div>
          <Field label="Date" htmlFor="filter-date" />
          <DatepickerWithIcon
            defaultSelectedDates={[
              values.periodStart as Date,
              values.periodEnd as Date,
            ]}
            onSelect={([from, to]) => {
              if (from && to) {
                handleSelectDates(from, to);
              }
            }}
          />
        </div>

        <div>
          <Field label="Document" htmlFor="filter-document" />
          <Input
            id="filter-document"
            type="text"
            {...getFieldProps('document')}
          />
        </div>
      </FormFilter>
      <FlexBox justifyContent="center" style={{ marginTop: '44px' }}>
        <CustomButton variation="primary-outline" onClick={close}>
          Cancel
        </CustomButton>
        <CustomButton variation="primary" type="submit" form="filter-form">
          Apply
        </CustomButton>
      </FlexBox>
    </CardFilter>
  );
};

export default FilterBox;
